import { Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import { MortgagePatchBodySchema } from "shared";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { NumberInput } from "@/components/form/NumberInput";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";
import { useMortgage } from "@/mortgages/hooks";

interface EditMortgageDetailsProps {
  mortgageID: number;
}

export const EditMortgageDetails = (props: EditMortgageDetailsProps) => {
  const dialog = useDialog();
  const mortgage = useMortgage(props.mortgageID);

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(MortgagePatchBodySchema),
    defaultValues: {
      balance: mortgage.data.balance,
      interestRate: mortgage.data.interestRate,
      escrowBalance: mortgage.data.escrowBalance,
      insuranceEffectiveDate:
        mortgage.data.insuranceEffectiveDate?.toString() ?? null,
      principalPayment: mortgage.data.payments.principal,
      interestPayment: mortgage.data.payments.interest,
      escrowPayment: mortgage.data.payments.escrow,
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (values) => {
    await Api.MortgagePatch(props.mortgageID, values);

    await queryClient.refetchQueries({
      queryKey: QueryKeys.Mortgage(props.mortgageID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Edit mortgage details</Dialog.Header>
      <Dialog.Body>
        <NumberInput
          control={control}
          name="balance"
          label="Mortgage balance"
        />
        <NumberInput
          control={control}
          dp={4}
          name="interestRate"
          label="Interest rate"
        />
        <NumberInput
          control={control}
          name="escrowBalance"
          label="Escrow balance"
        />
        <TextField
          type="date"
          control={control}
          name="insuranceEffectiveDate"
          label="Property insurance effective date"
        />
        <NumberInput
          control={control}
          name="principalPayment"
          label="Principal payment"
        />
        <NumberInput
          control={control}
          name="interestPayment"
          label="Interest payment"
        />
        <NumberInput
          control={control}
          name="escrowPayment"
          label="Escrow payment"
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
